import React from "react";
import { Assets, Colors, FontFamily, PrimaryButton, ProgressBar, FileUpload, Loader } from "utils/imports.utils";
import { ButtonSize, ButtonTypes, PropertiesTypes, UploadFileTypes } from "utils/interface.utils";
import Modal from "common_components/ui/modal/modal.ui";
import { formatPaiseToRupees, getImageDimension, getVideoDimension, isDuplicateTargetImage, isUnlimitedUser, toastifyError, useSetState, validateObjectFilePreflight, validateTargetImageDimensions } from "utils/functions.utils";
import { IMAGE_MAX_SIZE_MB, TARGET_PRICE_PAISE, VIDEO_MAX_SIZE_MB } from "utils/constant.utils";
import { STRINGS } from "constants/strings.constant";
import { RootState } from "store/store";
import { useDispatch, useSelector } from "react-redux";
import "./upload_file_card.component.scss";
import user from "models/user.model";
import object from "models/object.model";
interface IUploadFileCard {
    isOpen: boolean;
    onClose: () => void;
    setImages: (file: { image: string, targetImageDimension: { width: number, height: number } } | {image: string, imageDimension: { width: number, height: number }, objectId?: string}) => void;
    setVideo: (file: {video: string, videoDimension: { width: number, height: number }, objectId?: string}) => void;
    type: string;
    width?: string;
    height?: string;
    ensureTargetId?: () => Promise<string>;
    existingObjectId?: string;
}

const UploadFileCard = (props:IUploadFileCard) => {
    const { currentSelectedPropertyType, targetImageDataList, selectedTargetImageID } = useSelector((state: RootState) => state.editor)
    const [state, setState] = useSetState({
        isLoading:false,
    })

    // Videos and images have different limits, so the card picks the one for what it is uploading.
    const isVideoUpload = props.type === UploadFileTypes.VIDEO;
    const maxSizeMb = isVideoUpload ? VIDEO_MAX_SIZE_MB : IMAGE_MAX_SIZE_MB;
    // The exempt account has no size limit and is never charged, so the card shows neither the limit nor the price.
    const isUnlimited = isUnlimitedUser();

    const handleUploadTargetImage = async (file:File) => {
        try {
            const isDuplicate = await isDuplicateTargetImage(file, targetImageDataList, selectedTargetImageID);
            if (isDuplicate) {
                toastifyError("This image is already used as a target in this project");
                setState({isLoading:false})
                return;
            }

            const targetImageDimension = await getImageDimension(file) as { width: number, height: number };
            const dimensionError = validateTargetImageDimensions(targetImageDimension);
            if (dimensionError) {
                toastifyError(dimensionError);
                setState({isLoading:false})
                return;
            }

            const data = {
                files:[file]
            }
            const response = await user.uploadMultiple(data);
            const imageUrl = response.fileUrls[0];

            props.setImages({
                image:imageUrl,
                targetImageDimension: targetImageDimension as { width: number, height: number }
            })

            setState({isLoading:false})
        } catch (error) {
            toastifyError("Failed to upload target image");
            setState({isLoading:false})
        }
    }

    const handleUploadObjectImage = async (file:File) => {
        try {
            const imageDimensions = await getImageDimension(file);
            const targetId = await props.ensureTargetId!();
            const response = await object.uploadObject(targetId, 'image', file, props.existingObjectId);
            props.setImages({image:response.image as string, imageDimension:imageDimensions as { width: number, height: number }, objectId:response._id})
            setState({isLoading:false})
        } catch (error: any) {
            toastifyError(error?.response?.data?.message || "Failed to upload image object");
            setState({isLoading:false})
        }
    }

    const handleObjectVideoUpload = async (file:File) => {
        try {
            const videoDimensions = await getVideoDimension(file);
            const targetId = await props.ensureTargetId!();
            const response = await object.uploadObject(targetId, 'video', file, props.existingObjectId);
            props.setVideo({video:response.video as string, videoDimension:videoDimensions as { width: number, height: number }, objectId:response._id})
            setState({isLoading:false})
        } catch (error: any) {
            toastifyError(error?.response?.data?.message || "Failed to upload video object");
            setState({isLoading:false})
        }
    }

    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose}>
            <div className="upload_image_card" style={{width:props.width,height:props.height}}>
                <div className="upload_image_card_container">
                    { state.isLoading && 
                        <div className="upload_image_card_loader_container">
                            <div className="upload_image_card_loader_text">Uploading...</div>
                            <div className="upload_image_card_loader_gif">
                                <img src={Assets.activity_indicator} width="54px" height="54px" />
                            </div>
                        </div>
                    }                    
                    {/* { state.progress > 0 &&
                        <div className="upload_image_card_progress_bar_container">
                            <ProgressBar text="Compiling Target Image..." value={state.progress} max={100}/>
                        </div> 
                    } */}
                    { !state.isLoading &&
                        <React.Fragment>
                            <div>
                                <div className="upload_image_card_header" style={props.type === UploadFileTypes.VIDEO ? {marginBottom: "16px"} : undefined}>
                                    <div></div>
                                    <div className="upload_image_card_header_title">Upload {props.type}</div>
                                    <img onClick={props.onClose} src={Assets.close} alt="close" style={{width: "24px", height: "24px"}}/>
                                </div>
                                {props.type === UploadFileTypes.VIDEO && (
                                    <div className="upload_image_card_additional_info">
                                        <div className="upload_image_card_additional_info_title">Choose and upload your video</div>
                                        <div className="upload_image_card_additional_info_desc">Image with dark, bright areas and well-lit areas with unique features is preferred for better recognition while scanning.</div>
                                    </div>
                                )}
                            </div>
                            <FileUpload
                                fileTypes={
                                    currentSelectedPropertyType === PropertiesTypes.TARGET ? ["JPEG", "JPG", "PNG", "WEBP", "SVG", "TIFF", "AVIF"] :
                                    props.type === UploadFileTypes.IMAGE ? ["JPEG", "JPG", "PNG", "WEBP"] : ["MP4"]
                                }
                                maxFileSize={isUnlimited ? undefined : maxSizeMb}
                                handleChange={(file:File) => {
                                    const isTargetUpload = currentSelectedPropertyType === PropertiesTypes.TARGET;
                                    if (!isTargetUpload) {
                                        const preflightError = validateObjectFilePreflight(file, props.type === UploadFileTypes.VIDEO ? 'video' : 'image');
                                        if (preflightError) {
                                            toastifyError(preflightError);
                                            return;
                                        }
                                    }
                                    setState({isLoading:true})
                                    props.type === UploadFileTypes.VIDEO ? handleObjectVideoUpload(file) :
                                    isTargetUpload ?
                                    handleUploadTargetImage(file) :
                                    handleUploadObjectImage(file)
                                }}
                                handleSizeError={() => {
                                    // The drop zone passes a text message here, not the file, so the file's size isn't
                                    // available: say what the limit is instead.
                                    toastifyError(`${STRINGS.upload.fileTooLarge} ${maxSizeMb}MB.`)
                                    setState({isLoading:false})
                                }}
                                handleTypeError={(error:string) => {
                                    toastifyError(error)
                                    setState({isLoading:false})
                                }}
                            >
                                <div className="upload_image_card_body" onClick={(e:any) => {
                                    e.stopPropagation(); 
                                }} onClickCapture={(e:any) => {
                                    e.stopPropagation();
                                }}>
                                    <img src={props.type === UploadFileTypes.IMAGE ? Assets.image_upload : Assets.video_upload} alt="Upload image" style={{width: "44px", height: "44px"}}/>
                                    <div className="upload_image_card_body_content">
                                        <div className="upload_image_card_body_content_button">
                                            <PrimaryButton 
                                                text="Click to upload" 
                                                color={Colors.primaryTextColor} 
                                                size={ButtonSize.SMALL} 
                                                type={ButtonTypes.TEXT} 
                                                fontFamily={FontFamily.semiBold} 
                                                textDecorationColor={Colors.primaryTextColor} 
                                                lineHeight="24px"
                                                onClick={(e:any) => {
                                                    e.stopPropagation();
                                                }}
                                            /> 
                                            <span>or</span>
                                            <div>drag and drop</div>
                                        </div>
                                        {!isUnlimited && <div className="upload_image_card_body_content_desc">Max, {props.type} size : {maxSizeMb}MB</div>}
                                        {isVideoUpload && !isUnlimited && (
                                            <div className="upload_image_card_body_content_pricing">
                                                <div className="upload_image_card_body_content_pricing_note">{STRINGS.upload.videoPriceNote}</div>
                                                <div className="upload_image_card_body_content_pricing_tiers">
                                                    {`${STRINGS.upload.videoPriceUpTo} ${VIDEO_MAX_SIZE_MB}MB: ${formatPaiseToRupees(TARGET_PRICE_PAISE)}`}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </FileUpload>
                        </React.Fragment>
                    }
                </div>
            </div>
        </Modal>
    )
}

export default UploadFileCard;